import { useState, useEffect } from "react";
import { toast } from "sonner";
import { getOverrides, deleteOverride, updateOverride, getPartidasBalance, getPartidasPyg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Pencil, Trash2, FileText, ListFilter } from "lucide-react";

export default function ReglasAsignacionContent({ empresaId }) {
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [partidasBalance, setPartidasBalance] = useState([]);
  const [partidasPyg, setPartidasPyg] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingOverride, setEditingOverride] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state for editing
  const [formCodigo, setFormCodigo] = useState("");
  const [formTipoCodigo, setFormTipoCodigo] = useState("");
  const [formTipoDestino, setFormTipoDestino] = useState("");
  const [formPartida, setFormPartida] = useState("");

  useEffect(() => {
    loadData();
  }, [empresaId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [overridesRes, balanceRes, pygRes] = await Promise.all([
        getOverrides(empresaId),
        getPartidasBalance(),
        getPartidasPyg(),
      ]);
      setOverrides(overridesRes.data);
      setPartidasBalance(balanceRes.data);
      setPartidasPyg(pygRes.data);
    } catch (error) {
      toast.error("Error al cargar reglas de asignación");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (override) => {
    setEditingOverride(override);
    setFormCodigo(override.codigo);
    setFormTipoCodigo(override.tipo_codigo);
    setFormTipoDestino(override.tipo_destino);
    setFormPartida(override.partida_pgc);
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!formCodigo || !formTipoCodigo || !formTipoDestino || !formPartida) {
      toast.error("Todos los campos son obligatorios");
      return;
    }

    setSaving(true);
    try {
      await updateOverride(editingOverride.id, {
        codigo: formCodigo,
        tipo_codigo: formTipoCodigo,
        tipo_destino: formTipoDestino,
        partida_pgc: formPartida,
      });
      toast.success("Regla actualizada correctamente");
      setEditDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error("Error al actualizar la regla");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (overrideId) => {
    try {
      await deleteOverride(overrideId);
      toast.success("Regla eliminada");
      loadData();
    } catch (error) {
      toast.error("Error al eliminar la regla");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getPartidasOptions = () => {
    if (formTipoDestino === "BALANCE") {
      return partidasBalance;
    } else if (formTipoDestino === "PYG") {
      return partidasPyg;
    }
    return [];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reglas-asignacion-content">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ListFilter className="h-5 w-5 text-muted-foreground" />
              <div>
                <CardTitle>Reglas de Asignación</CardTitle>
                <CardDescription>
                  Reglas personalizadas para clasificar cuentas contables automáticamente
                </CardDescription>
              </div>
            </div>
            <Badge variant="secondary">{overrides.length} reglas</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {overrides.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-medium text-lg mb-2">Sin reglas de asignación</h3>
              <p className="text-muted-foreground text-sm max-w-md mx-auto">
                Las reglas se crean automáticamente cuando asignas manualmente una cuenta 
                en la pantalla de revisión y marcas la opción "Guardar como regla".
              </p>
            </div>
          ) : (
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[120px]">Código</TableHead>
                    <TableHead className="w-[100px]">Tipo Código</TableHead>
                    <TableHead className="w-[100px]">Destino</TableHead>
                    <TableHead>Partida Asignada</TableHead>
                    <TableHead className="w-[120px]">Fecha</TableHead>
                    <TableHead className="w-[100px] text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {overrides.map((override) => (
                    <TableRow key={override.id} data-testid={`override-row-${override.id}`}>
                      <TableCell className="font-mono font-medium">{override.codigo}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {override.tipo_codigo === "full" ? "Completo" : "3 dígitos"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={override.tipo_destino === "BALANCE" ? "default" : "secondary"}
                          className="text-xs"
                        >
                          {override.tipo_destino}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{override.partida_pgc}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(override.fecha_creacion)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(override)}
                            data-testid={`edit-override-${override.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-destructive hover:text-destructive"
                                data-testid={`delete-override-${override.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>¿Eliminar regla?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Se eliminará la regla para el código <strong>{override.codigo}</strong>. 
                                  Las cuentas ya clasificadas no se verán afectadas.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDelete(override.id)}
                                  className="bg-destructive text-destructive-foreground"
                                >
                                  Eliminar
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Regla de Asignación</DialogTitle>
            <DialogDescription>
              Modifica los datos de la regla de clasificación
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="codigo">Código de cuenta</Label>
                <Input
                  id="codigo"
                  value={formCodigo}
                  onChange={(e) => setFormCodigo(e.target.value)}
                  placeholder="Ej: 430, 4300001"
                  data-testid="edit-codigo-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tipo_codigo">Tipo de código</Label>
                <Select value={formTipoCodigo} onValueChange={setFormTipoCodigo}>
                  <SelectTrigger data-testid="edit-tipo-codigo-select">
                    <SelectValue placeholder="Seleccionar tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cuenta3">3 dígitos</SelectItem>
                    <SelectItem value="full">Código completo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="tipo_destino">Tipo de destino</Label>
              <Select 
                value={formTipoDestino} 
                onValueChange={(value) => {
                  setFormTipoDestino(value);
                  setFormPartida(""); // Reset partida when changing destination type
                }}
              >
                <SelectTrigger data-testid="edit-tipo-destino-select">
                  <SelectValue placeholder="Seleccionar destino" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="BALANCE">Balance</SelectItem>
                  <SelectItem value="PYG">Pérdidas y Ganancias</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="partida">Partida asignada</Label>
              <Select value={formPartida} onValueChange={setFormPartida}>
                <SelectTrigger data-testid="edit-partida-select">
                  <SelectValue placeholder="Seleccionar partida" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {getPartidasOptions().map((partida) => (
                    <SelectItem key={partida} value={partida}>
                      {partida}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving} data-testid="save-edit-btn">
              {saving ? "Guardando..." : "Guardar cambios"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
